import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useGamification } from '../../contexts/GamificationContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTrophy, FiUsers, FiBriefcase, FiCrown, FiMedal, FiAward, FiTarget, FiActivity } = FiIcons;

const Leaderboard = ({ userRole = 'candidate' }) => {
  const { leaderboard } = useGamification();
  const [filter, setFilter] = useState('all');

  // Filter the global leaderboard based on selection
  const filteredLeaderboard = leaderboard
    .filter(entry => {
      if (filter === 'all') return true;
      if (filter === 'candidates') return entry.role === 'candidate';
      if (filter === 'companies') return entry.role === 'company';
      if (filter === 'hosts') return entry.role === 'host';
      return true;
    })
    .sort((a, b) => b.metricValue - a.metricValue) // Ensure robust sorting by metric
    .slice(0, 10); // Top 10

  const getRankIcon = (index) => {
    switch (index) {
      case 0: return { icon: FiCrown, color: 'text-yellow-500' };
      case 1: return { icon: FiMedal, color: 'text-gray-400' };
      case 2: return { icon: FiAward, color: 'text-orange-600' };
      default: return { icon: FiTrophy, color: 'text-gray-300' };
    }
  };

  const getRoleIcon = (role) => {
    return role === 'candidate' ? FiUsers : role === 'company' ? FiBriefcase : FiActivity;
  };

  const getMetricDescription = (role) => {
    if (role === 'candidate') return 'Unique Scans';
    if (role === 'company') return 'Evaluations';
    return 'Points';
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }} 
      animate={{ opacity: 1, y: 0 }} 
      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Live Leaderboard</h3>
          <p className="text-sm text-gray-500 mt-1">Top performers based on activity</p>
        </div>
        <div className="flex items-center space-x-2">
          <select 
            value={filter} 
            onChange={(e) => setFilter(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Participants</option>
            <option value="candidates">Most Active Candidates</option>
            <option value="companies">Top Evaluating Companies</option>
          </select>
        </div>
      </div>

      <div className="space-y-3">
        {filteredLeaderboard.map((entry, index) => {
          const rankIcon = getRankIcon(index);
          const roleIcon = getRoleIcon(entry.role);
          const isTopThree = index < 3;

          return (
            <motion.div 
              key={`${entry.role}-${entry.id}`}
              initial={{ opacity: 0, x: -20 }} 
              animate={{ opacity: 1, x: 0 }} 
              transition={{ delay: index * 0.1 }}
              className={`flex items-center justify-between p-4 rounded-lg border ${
                isTopThree 
                  ? 'bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-200' 
                  : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className="flex items-center space-x-4">
                <div className="flex items-center justify-center w-8 h-8">
                  <SafeIcon 
                    icon={rankIcon.icon} 
                    className={`text-xl ${rankIcon.color}`} 
                  />
                  <span className="sr-only">Rank {index + 1}</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-full ${
                    entry.role === 'candidate' ? 'bg-blue-100 text-blue-600' : 
                    entry.role === 'company' ? 'bg-green-100 text-green-600' : 'bg-purple-100 text-purple-600'
                  }`}>
                    <SafeIcon icon={roleIcon} className="text-sm" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{entry.name}</p>
                    <p className="text-xs text-gray-500 capitalize">{entry.role}</p>
                  </div>
                </div>
              </div>
              
              <div className="text-right">
                <div className="flex flex-col items-end">
                  <span className="text-lg font-bold text-gray-900">
                    {entry.metricValue}
                  </span>
                  <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">
                    {entry.metricLabel}
                  </span>
                </div>
              </div>
            </motion.div>
          );
        })}

        {filteredLeaderboard.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No activity recorded yet for this category.
          </div>
        )}
      </div>

      <div className="mt-6 pt-4 border-t border-gray-100 text-center">
        <p className="text-xs text-gray-500">
          Rankings are updated in real-time based on confirmed interactions.
        </p>
      </div>
    </motion.div>
  );
};

export default Leaderboard;